import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api';
import Icon from '../../components/ui/Icon';
import { usePopup } from '../../context/PopupContext';
import { clearAuth, getUser } from '../../utils/authUtil';
import ThemeSetting from '../../components/ui/ThemeSetting';

/** Admin / super-admin account settings — deliberately small: read-only identity, change password
 * (the same POST /auth/change-password every role uses), sign out. Mirrors pages/StudentSettings.js. */
export default function AdminSettings() {
  const navigate = useNavigate();
  const { showToast, showConfirm } = usePopup();
  const user = getUser();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); if (problem) setProblem(''); };

  const ready = form.current && form.next.length >= 8 && form.next === form.confirm;
  const mismatch = form.confirm.length > 0 && form.next !== form.confirm;

  const changePassword = async (e) => {
    e.preventDefault();
    if (!ready || saving) return;
    setSaving(true); setProblem('');
    try {
      await API.post('/auth/change-password', { current_password: form.current, new_password: form.next });
      setForm({ current: '', next: '', confirm: '' });
      showToast('Password updated.', { tone: 'success' });
    } catch (err) {
      setProblem(err.response?.data?.message || (err.response ? 'Could not update your password.' : 'You appear to be offline. Try again when you\'re connected.'));
    } finally { setSaving(false); }
  };

  const logout = async () => { if (await showConfirm('Log out', 'Are you sure you want to log out of Zaptude?')) { clearAuth(); navigate('/login'); } };

  return (
    <div className="z-page z-page--narrow settings-page">
      <h1 className="z-sr-only">Settings</h1>
      <section className="z-section" aria-labelledby="aset-account">
        <h2 className="z-section-title" id="aset-account">Account</h2>
        <div className="z-list">
          <div className="z-list-row settings-static"><span className="z-list-row-body"><span className="z-list-row-sub">Name</span><span className="z-list-row-title">{user?.name}</span></span></div>
          <div className="z-list-row settings-static"><span className="z-list-row-body"><span className="z-list-row-sub">Email</span><span className="z-list-row-title settings-wrap">{user?.email}</span></span></div>
          {user?.collegeName && <div className="z-list-row settings-static"><span className="z-list-row-body"><span className="z-list-row-sub">College</span><span className="z-list-row-title">{user.collegeName}</span></span></div>}
        </div>
      </section>

      <ThemeSetting />

      <section className="z-section" aria-labelledby="aset-password">
        <h2 className="z-section-title" id="aset-password">Change password</h2>
        <form className="settings-form z-surface" onSubmit={changePassword} noValidate>
          <div className="z-field">
            <label className="z-label" htmlFor="aset-current">Current password</label>
            <input id="aset-current" className="z-input" type="password" autoComplete="current-password" value={form.current} onChange={set('current')} />
          </div>
          <div className="z-field">
            <label className="z-label" htmlFor="aset-next">New password</label>
            <input id="aset-next" className="z-input" type="password" autoComplete="new-password" value={form.next} onChange={set('next')} aria-describedby="aset-next-help" />
            <span className="z-help" id="aset-next-help">At least 8 characters, with a letter and a number.</span>
          </div>
          <div className="z-field">
            <label className="z-label" htmlFor="aset-confirm">Confirm new password</label>
            <input id="aset-confirm" className="z-input" type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} aria-invalid={mismatch} />
            {mismatch && <span className="z-error-text" role="alert">The passwords don't match.</span>}
          </div>
          {problem && <div className="z-error-text" role="alert">{problem}</div>}
          <button type="submit" className="z-btn z-btn--primary z-btn--block" disabled={!ready || saving}>
            <Icon name="key" size={18} /> {saving ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </section>

      <section className="z-section" aria-labelledby="aset-more">
        <h2 className="z-section-title" id="aset-more">More</h2>
        <div className="z-list">
          <button type="button" className="z-list-row z-list-row--danger" onClick={logout}>
            <span className="z-list-row-body"><span className="z-list-row-title">Log out</span></span>
            <Icon name="logout" size={18} />
          </button>
        </div>
      </section>
    </div>
  );
}
